/* ============================================================
   SAN Sound — Support Hub router
   Routes the Support page: product tabs, order/policy tabs,
   compact group accordions and a global search across every FAQ.
   Progressive enhancement: with JS off, every panel renders as before.
   ============================================================ */

(function () {
  'use strict';

  var MIN_QUERY = 2;
  var DEBOUNCE = 140;
  var HTML = document.documentElement;

  function normalize(value) {
    return (value || '')
      .toLowerCase()
      .replace(/[‘’“”]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function SupportHub(router) {
    this.router = router;
    this.tabs = toArray(router.querySelectorAll('[data-support-target]'));
    this.form = router.querySelector('[data-support-search]');
    this.input = router.querySelector('[data-support-input]');
    this.clear = router.querySelector('[data-support-clear]');
    this.status = router.querySelector('[data-support-status]');
    this.defaultStatus = this.status ? this.status.getAttribute('data-default') || '' : '';
    this.panels = toArray(document.querySelectorAll('[data-support-panel]'));
    this.entries = [];
    this.groups = [];
    this.activeKey = null;
    this.searching = false;
    this.timer = null;

    this.buildIndex();
    this.ensureFormAnchor();
    this.bind();

    /* Routing must never be able to take the whole hub down. */
    try {
      this.restoreFromHash();
    } catch (error) {
      this.activate(null, false);
    }
  }

  SupportHub.prototype.buildIndex = function () {
    var self = this;

    this.groups = toArray(document.querySelectorAll('details[data-support-group]')).map(function (group) {
      return { el: group, openByDefault: group.hasAttribute('open') };
    });

    this.panels.forEach(function (panel) {
      var aliases = normalize(
        [
          panel.getAttribute('data-support-panel'),
          panel.getAttribute('data-support-label'),
          panel.getAttribute('data-support-aliases')
        ].join(' ')
      );

      toArray(panel.querySelectorAll('[data-support-question]')).forEach(function (item) {
        var group = item.closest('[data-support-group]');
        var groupName = group ? normalize(group.getAttribute('data-support-group')) : '';

        self.entries.push({
          panel: panel,
          group: group && group.tagName === 'DETAILS' ? group : null,
          el: item,
          haystack: normalize(item.textContent) + ' ' + groupName + ' ' + aliases
        });
      });
    });
  };

  /* The support form lives in its own Shopify section whose DOM id is generated
     at render time. Drop a stable anchor in front of it so "Still need help?"
     links can point at #support-form. */
  SupportHub.prototype.ensureFormAnchor = function () {
    if (document.getElementById('support-form')) return;

    var form = document.querySelector('form[action*="/contact"]');
    if (!form) return;

    var host = form.closest('[id^="shopify-section"]') || form;
    var anchor = document.createElement('span');
    anchor.id = 'support-form';
    anchor.setAttribute('aria-hidden', 'true');
    anchor.style.display = 'block';
    anchor.style.position = 'relative';
    anchor.style.top = '-96px';
    host.parentNode.insertBefore(anchor, host);
  };

  SupportHub.prototype.panelFor = function (key) {
    for (var i = 0; i < this.panels.length; i++) {
      var panel = this.panels[i];
      if (panel.getAttribute('data-support-panel') === key) return panel;

      var aliases = (panel.getAttribute('data-support-aliases') || '').split(',');
      for (var a = 0; a < aliases.length; a++) {
        if (normalize(aliases[a]) && normalize(aliases[a]) === normalize(key)) return panel;
      }
    }
    return null;
  };

  SupportHub.prototype.bind = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        var key = tab.getAttribute('data-support-target');
        if (self.searching) self.resetSearch(false);
        self.activate(key === self.activeKey ? null : key, true);
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.runSearch();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', function () {
        clearTimeout(self.timer);
        self.timer = setTimeout(function () {
          self.runSearch();
        }, DEBOUNCE);
      });
    }

    if (this.clear) {
      this.clear.addEventListener('click', function (event) {
        event.preventDefault();
        self.resetSearch(true);
      });
    }

    window.addEventListener('hashchange', function () {
      self.restoreFromHash();
    });

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', function (event) {
        var node = event.target;
        var panel = node && node.closest ? node.closest('[data-support-panel]') : null;
        var group = node && node.closest ? node.closest('details[data-support-group]') : null;
        if (group) group.setAttribute('open', '');
        if (panel) self.activate(panel.getAttribute('data-support-panel'), false);
      });
      document.addEventListener('shopify:section:select', function (event) {
        var panel = event.target.querySelector ? event.target.querySelector('[data-support-panel]') : null;
        if (panel) self.activate(panel.getAttribute('data-support-panel'), false);
      });
    }
  };

  /* --- Tab routing ------------------------------------------------------ */

  SupportHub.prototype.activate = function (key, updateHash) {
    var self = this;
    var target = key ? this.panelFor(key) : null;
    var family = target ? target.getAttribute('data-support-family') : null;

    this.activeKey = target ? target.getAttribute('data-support-panel') : null;

    this.panels.forEach(function (panel) {
      var shared = panel.getAttribute('data-support-shared');
      var isTarget = panel === target;
      var isShared = !!(target && shared && shared === family);
      panel.classList.toggle('is-active', isTarget || isShared);
    });

    this.tabs.forEach(function (tab) {
      tab.setAttribute('aria-pressed', tab.getAttribute('data-support-target') === self.activeKey ? 'true' : 'false');
    });

    this.setStatus(this.activeKey ? '' : this.defaultStatus);

    if (updateHash && window.history && window.history.replaceState) {
      window.history.replaceState(null, '', this.activeKey ? '#support-' + this.activeKey : window.location.pathname);
    }
  };

  SupportHub.prototype.restoreFromHash = function () {
    var raw = (window.location.hash || '').replace('#', '');
    if (!raw) {
      this.activate(null, false);
      return;
    }

    var key = raw.replace(/^support-/, '').replace(/^faq-/, '');
    var panel = this.panelFor(key);
    if (!panel) return;

    this.activate(panel.getAttribute('data-support-panel'), false);

    try {
      this.scrollTo(panel);
    } catch (error) {
      /* scrolling is a nicety, never a blocker */
    }
  };

  SupportHub.prototype.scrollTo = function (panel) {
    if (typeof window.scrollTo !== 'function') return;

    var header = document.querySelector('header.header');
    var offset = header ? header.clientHeight + 16 : 16;
    var top = panel.getBoundingClientRect().top + (window.scrollY || 0) - offset;
    var reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: top < 0 ? 0 : top, behavior: reduced ? 'auto' : 'smooth' });
  };

  /* --- Search ----------------------------------------------------------- */

  SupportHub.prototype.runSearch = function () {
    var raw = this.input ? this.input.value : '';
    var query = normalize(raw);

    if (this.form) this.form.classList.toggle('is-filled', raw.length > 0);

    if (query.length < MIN_QUERY) {
      this.resetSearch(false);
      return;
    }

    var tokens = query.split(' ');
    var matches = 0;

    this.panels.forEach(function (panel) {
      panel.classList.remove('has-matches');
    });

    this.entries.forEach(function (entry) {
      var hit = tokens.every(function (token) {
        return entry.haystack.indexOf(token) !== -1;
      });

      entry.el.classList.toggle('has-match', hit);

      if (hit) {
        matches++;
        if (entry.group) {
          entry.group.classList.add('has-matches');
          entry.group.setAttribute('open', '');
        }
        entry.panel.classList.add('has-matches');
      }
    });

    // Groups with no hit at all lose the flag.
    toArray(document.querySelectorAll('details[data-support-group]')).forEach(function (group) {
      if (!group.querySelector('.accordion.has-match')) {
        group.classList.remove('has-matches');
        group.removeAttribute('open');
      }
    });

    this.searching = true;
    HTML.classList.add('support-hub-searching');

    this.setStatus(
      matches === 0
        ? 'No matches for “' + raw.trim() + '”. Try fewer words, or use the support form below.'
        : matches + (matches === 1 ? ' answer' : ' answers') + ' for “' + raw.trim() + '”'
    );
  };

  SupportHub.prototype.resetSearch = function (clearInput) {
    if (clearInput && this.input) {
      this.input.value = '';
      this.input.focus();
    }

    if (this.form) this.form.classList.toggle('is-filled', !!(this.input && this.input.value.length));

    this.searching = false;
    HTML.classList.remove('support-hub-searching');

    this.entries.forEach(function (entry) {
      entry.el.classList.remove('has-match');
    });

    this.groups.forEach(function (group) {
      group.el.classList.remove('has-matches');
      if (group.openByDefault) {
        group.el.setAttribute('open', '');
      } else {
        group.el.removeAttribute('open');
      }
    });

    this.panels.forEach(function (panel) {
      panel.classList.remove('has-matches');
    });

    this.setStatus(this.activeKey ? '' : this.defaultStatus);
  };

  SupportHub.prototype.setStatus = function (message) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.hidden = !message;
  };

  /* --- Boot ------------------------------------------------------------- */

  function init() {
    toArray(document.querySelectorAll('[data-support-hub]')).forEach(function (router) {
      if (router.dataset.supportHubReady === 'true') return;
      router.dataset.supportHubReady = 'true';

      try {
        new SupportHub(router);
      } catch (error) {
        /* Leave the page usable: show everything rather than nothing. */
        router.dataset.supportHubReady = 'false';
        document.documentElement.classList.remove('support-hub-js');
        if (window.console && window.console.warn) window.console.warn('[support-hub]', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    toArray(document.querySelectorAll('[data-support-hub]')).forEach(function (router) {
      router.dataset.supportHubReady = 'false';
    });
    init();
  });
})();
